/**
 * Pane mapping for the profile editor (PTEC-585).
 *
 * The editor keeps every pane mounted and merely hides the inactive ones, so
 * react-hook-form validates the whole form on submit. That is deliberate, but it
 * means a required field on a pane the user cannot see blocks the save while its
 * error message renders inside `display: none`, and Save appears to do nothing.
 *
 * These helpers let the submit handler find the offending field, bring its pane
 * into view and name it. They are pure so they can be tested without mounting
 * the page.
 */
export type EditPane = 'profile' | 'education' | 'address' | 'contact' | 'preferences';

/** Pane order as the user sees it in the tab strip. */
export const PANE_ORDER: EditPane[] = ['profile', 'education', 'address', 'contact', 'preferences'];

export const PANE_LABELS: Record<EditPane, string> = {
	profile: 'Profile',
	education: 'Education',
	address: 'Address',
	contact: 'Contact',
	preferences: 'Preferences'
};

/**
 * Which pane owns a field, keyed by the field path react-hook-form reports.
 * Nested paths (`address.line1`, `educationHistory.0.institution`) match on
 * their first segment.
 */
export const PANE_BY_FIELD: Record<string, EditPane> = {
	firstName: 'profile',
	lastName: 'profile',
	preferredName: 'profile',
	dateOfBirth: 'profile',
	citizenship: 'profile',
	educationHistory: 'education',
	address: 'address',
	currency: 'address',
	email: 'contact',
	phone: 'contact',
	language: 'preferences'
};

export const paneForField = (field: string): EditPane | undefined => PANE_BY_FIELD[field.split('.')[0]];

/**
 * The first field with an error, in the order the panes appear to the user, so
 * we land on the earliest problem rather than whichever key the resolver
 * happened to emit first. Fields with no known pane sort last: they are still
 * reported, but a mapped field is a better place to send someone.
 */
export const firstInvalidField = (errors: Record<string, unknown> | undefined): string | undefined => {
	const fields = Object.keys(errors ?? {});
	if (fields.length === 0) {
		return undefined;
	}
	const rank = (field: string): number => {
		const pane = paneForField(field);
		return pane ? PANE_ORDER.indexOf(pane) : PANE_ORDER.length;
	};
	return [...fields].sort((a, b) => rank(a) - rank(b))[0];
};
